(() => {
  const MAX_STORED_HTML_LENGTH = 100000;
  const REVEAL_DELAY_MS = 5000;
  const STORED_CARD_SELECTOR = 'li.product-discovery__item[data-recent-product-card]';

  class RecentlyViewedRecovery extends HTMLElement {
    constructor() {
      super();
      this.onStorage = this.onStorage.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onCollectionUpdated = this.onCollectionUpdated.bind(this);
      this.onFirstScroll = this.onFirstScroll.bind(this);
      this.schedulePlacement = this.schedulePlacement.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('resize', this.schedulePlacement, { passive: true });
      document.addEventListener('cc:collection-updated', this.onCollectionUpdated);

      this.armRevealDelay();
      this.claimMostRecentProductForCurrentCollection();
      this.refresh();
    }

    disconnectedCallback() {
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('resize', this.schedulePlacement);
      window.removeEventListener('scroll', this.onFirstScroll);
      document.removeEventListener('cc:collection-updated', this.onCollectionUpdated);
      this.gridObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      window.cancelAnimationFrame(this.placementFrame);
      window.clearTimeout(this.revealTimer);
      delete this.dataset.initialized;
    }

    get storageKey() {
      return this.dataset.storageKey || '';
    }

    get collectionClaimKey() {
      return this.storageKey ? `${this.storageKey}:recovery-collection` : '';
    }

    get collectionPath() {
      return window.location.pathname.replace(/\/+$/, '') || '/';
    }

    findGrid() {
      const container = this.closest('#ProductGridContainer');
      return container?.querySelector('ul#product-grid') || null;
    }

    readMostRecentProduct() {
      if (!this.storageKey) return null;

      try {
        const stored = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(stored)) return null;

        return (
          stored
            .filter(
              (entry) =>
                entry &&
                typeof entry.handle === 'string' &&
                typeof entry.html === 'string' &&
                entry.html.length > 0 &&
                entry.html.length < MAX_STORED_HTML_LENGTH
            )
            .sort((first, second) => (Number(second.viewedAt) || 0) - (Number(first.viewedAt) || 0))[0] || null
        );
      } catch (_error) {
        return null;
      }
    }

    refresh() {
      if (!this.revealDelayElapsed) {
        this.removeCard();
        return;
      }

      const nextGrid = this.findGrid();
      if (!nextGrid) {
        this.removeCard();
        return;
      }

      if (this.grid !== nextGrid) {
        this.gridObserver?.disconnect();
        this.grid = nextGrid;
        this.gridObserver = new MutationObserver(this.schedulePlacement);
        this.gridObserver.observe(this.grid, { attributes: true, attributeFilter: ['class'] });
      }

      const recentProduct = this.readMostRecentProduct();
      if (!recentProduct || this.isClaimedForAnotherCollection(recentProduct)) {
        this.removeCard();
        return;
      }

      if (this.card?.dataset.productHandle === recentProduct.handle) {
        this.schedulePlacement();
        return;
      }

      this.removeCard();
      this.card = this.createCard(recentProduct);
      if (!this.card) return;

      this.placeCard();
      this.claimCurrentCollection(recentProduct);
      this.observeVisibility();
    }

    isClaimedForAnotherCollection(entry) {
      if (!this.collectionClaimKey) return false;

      try {
        const claim = JSON.parse(window.sessionStorage.getItem(this.collectionClaimKey) || 'null');
        return (
          claim?.handle === entry.handle &&
          typeof claim.collectionPath === 'string' &&
          claim.collectionPath !== this.collectionPath
        );
      } catch (_error) {
        return false;
      }
    }

    claimCurrentCollection(entry) {
      if (!this.collectionClaimKey) return;

      try {
        window.sessionStorage.setItem(
          this.collectionClaimKey,
          JSON.stringify({ handle: entry.handle, collectionPath: this.collectionPath })
        );
      } catch (_error) {
        return;
      }
    }

    claimMostRecentProductForCurrentCollection() {
      const recentProduct = this.readMostRecentProduct();
      if (!recentProduct || this.isClaimedForAnotherCollection(recentProduct)) return;
      this.claimCurrentCollection(recentProduct);
    }

    createCard(entry) {
      const template = document.createElement('template');
      template.innerHTML = entry.html.trim();
      const card = template.content.firstElementChild;

      if (!card?.matches(STORED_CARD_SELECTOR)) return null;

      card.querySelectorAll('script, iframe, object, embed').forEach((element) => element.remove());
      card.querySelectorAll('*').forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
          if (attribute.name.toLowerCase().startsWith('on')) element.removeAttribute(attribute.name);
        });
      });

      const modalOpener = card.querySelector('.cc-quick-add modal-opener');
      const button = modalOpener?.querySelector('button[data-product-url]');
      const modalSelector = modalOpener?.getAttribute('data-modal') || '';

      if (!button || !/^#QuickAdd-[A-Za-z0-9_-]+$/.test(modalSelector)) return null;

      // Current cards use the shared dialog; stored cards from older versions may include their own.
      const modal = card.querySelector(`[id="${modalSelector.slice(1)}"]`) || document.querySelector(modalSelector);
      if (!modal?.matches('quick-add-modal')) return null;

      try {
        const productUrl = new URL(button.dataset.productUrl, window.location.origin);
        if (productUrl.origin !== window.location.origin || !productUrl.pathname.includes('/products/')) return null;
        button.dataset.productUrl = `${productUrl.pathname}${productUrl.search}`;
      } catch (_error) {
        return null;
      }

      const productTitle = card.querySelector('.card__heading .full-unstyled-link')?.textContent.trim() || '';
      const buttonLabel = button.disabled ? this.dataset.soldOutLabel : this.dataset.buttonLabel;
      const loadingSpinner = button.querySelector('.loading__spinner');
      if (!buttonLabel || !loadingSpinner) return null;

      button.querySelector('.cc-quick-add__plus')?.remove();
      button.classList.remove('cc-quick-add__trigger');
      button.classList.add(
        'button',
        'button--full-width',
        'button--secondary',
        'product-form__submit',
        'cc-recently-viewed-recovery__button'
      );
      button.setAttribute('type', 'button');
      button.setAttribute('aria-label', productTitle ? `${buttonLabel}: ${productTitle}` : buttonLabel);

      const visibleLabel = document.createElement('span');
      visibleLabel.className = 'cc-recently-viewed-recovery__button-label';
      visibleLabel.textContent = buttonLabel;
      button.insertBefore(visibleLabel, loadingSpinner);

      const panel = document.createElement('div');
      panel.className = 'cc-recently-viewed-recovery__panel';

      const heading = document.createElement('p');
      heading.className = 'cc-recently-viewed-recovery__heading';
      heading.textContent = this.dataset.heading;

      panel.append(heading, modalOpener);
      card.append(panel);
      card.classList.remove('product-discovery__item');
      card.classList.add('grid__item', 'cc-recently-viewed-recovery');
      card.removeAttribute('style');
      card.removeAttribute('data-recent-product-card');
      card.dataset.productHandle = entry.handle;

      return card;
    }

    removeCard() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
      this.card?.remove();
      this.card = null;
      this.placementReference = null;
    }

    onStorage(event) {
      if (event.key !== this.storageKey) return;
      this.claimMostRecentProductForCurrentCollection();
      this.refresh();
    }

    onPageShow(event) {
      if (event.persisted) this.armRevealDelay();
      this.claimMostRecentProductForCurrentCollection();
      this.refresh();
    }

    armRevealDelay() {
      window.clearTimeout(this.revealTimer);
      this.revealTimer = null;
      this.revealDelayElapsed = false;
      window.removeEventListener('scroll', this.onFirstScroll);
      window.addEventListener('scroll', this.onFirstScroll, { passive: true });
      this.removeCard();
    }

    onFirstScroll() {
      if (this.revealTimer || this.revealDelayElapsed) return;

      window.removeEventListener('scroll', this.onFirstScroll);
      this.revealTimer = window.setTimeout(() => {
        this.revealTimer = null;
        this.revealDelayElapsed = true;
        this.refresh();
      }, REVEAL_DELAY_MS);
    }

    onCollectionUpdated() {
      window.requestAnimationFrame(() => this.refresh());
    }

    schedulePlacement() {
      window.cancelAnimationFrame(this.placementFrame);
      this.placementFrame = window.requestAnimationFrame(() => this.placeCard());
    }

    getColumnCount() {
      const selectedView = Array.from(this.grid.classList)
        .map((className) => className.match(/^cc-grid--(\d+)$/))
        .find(Boolean);

      if (selectedView) return Number(selectedView[1]);

      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const fallbackPattern = desktop ? /^grid--(\d+)-col-desktop$/ : /^grid--(\d+)-col-tablet-down$/;
      const fallbackView = Array.from(this.grid.classList)
        .map((className) => className.match(fallbackPattern))
        .find(Boolean);

      return fallbackView ? Number(fallbackView[1]) : desktop ? 4 : 2;
    }

    findAheadReference(productItems) {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const placementLine = viewportHeight * 1.35;

      return productItems.find((item) => item.getBoundingClientRect().top >= placementLine) || null;
    }

    placeCard() {
      if (!this.grid || !this.card) return;

      const productItems = Array.from(this.grid.children).filter(
        (item) => item.matches('li.grid__item') && item !== this.card
      );
      if (!this.placementReference?.isConnected || this.placementReference.parentElement !== this.grid) {
        this.placementReference = this.findAheadReference(productItems);
      }

      const referenceItem = this.placementReference;

      if (this.card.parentElement !== this.grid || this.card.nextElementSibling !== referenceItem) {
        this.grid.insertBefore(this.card, referenceItem);
      }

      this.updateImageSizes();
    }

    updateImageSizes() {
      if (!this.card) return;

      const columnCount = this.getColumnCount();
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const desktopSizes = {
        2: 'calc((100vw - 8rem) / 2)',
        4: 'calc((100vw - 10rem) / 4)',
        6: 'calc((100vw - 13rem) / 6)',
      };
      const mobileSizes = {
        1: 'calc(100vw - 3rem)',
        2: 'calc((100vw - 3rem) / 2)',
        3: 'calc((100vw - 4rem) / 3)',
      };
      const imageSizes = (desktop ? desktopSizes : mobileSizes)[columnCount];

      if (!imageSizes) return;
      this.card.querySelectorAll('.card__media img').forEach((image) => image.setAttribute('sizes', imageSizes));
    }

    observeVisibility() {
      if (!this.card) return;

      if (!('IntersectionObserver' in window)) {
        this.card.classList.add('is-visible');
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.card?.classList.add('is-visible');
          observer.disconnect();
        },
        { rootMargin: '0px 0px 65% 0px', threshold: 0.01 }
      );
      this.visibilityObserver.observe(this.card);
    }
  }

  if (!customElements.get('recently-viewed-recovery')) {
    customElements.define('recently-viewed-recovery', RecentlyViewedRecovery);
  }
})();
